import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MinimapCanvas } from './components/MinimapCanvas';
import { useMinimapScene } from './hooks/useMinimapScene';
import { useScrollParent } from './hooks/useScrollParent';
import { BAR_PAD_X, RAIL_PAD, RAIL_WIDTH } from './utils/constants';
import { boxForPointer } from './utils/dragMath';
import { interpolate } from './utils/layoutEngine';

/**
 * DocMinimap — a right-edge navigator for the doc editor, conceptually the
 * same as the flowchart minimap / Tiedown's transcript scroll navigator.
 *
 * The mini doc is "to scale WITH ITSELF", not with the page's pixels: a line
 * of text is always 1px with a 1px gap, blocks are separated by a fixed 2px,
 * callout padding compresses to 2px — an idealized miniature of the
 * document's SHAPE rather than a shrunken screenshot. Because mini-space is
 * therefore non-linear in scroll-space, a piecewise-linear anchor map
 * translates between the two for the viewport window, clicks and drags.
 *
 * It attaches to the editor's nearest scrollable ancestor and portals to
 * document.body with fixed positioning so transformed modal ancestors can't
 * skew its coordinates. The native scrollbar is hidden while attached — this
 * IS the scrollbar.
 */
export const DocMinimap = ({ editorRootRef }) => {
  const [scrollEl, setScrollEl] = useScrollParent(editorRootRef);
  const dragRef = useRef(null);
  const { rect, scene, metrics, finishDrag } = useMinimapScene(
    editorRootRef,
    scrollEl,
    setScrollEl,
    dragRef
  );
  const [dragging, setDragging] = useState(false);
  const trackRef = useRef(null);
  const viewportRef = useRef(null);
  const dragBoxRef = useRef(null);
  const endDragRef = useRef(null);

  useEffect(() => () => endDragRef.current?.(), []);

  if (!scrollEl || !rect || rect.height <= 0 || scene.totalH <= 0) return null;

  const scrollable = metrics.scrollHeight - metrics.clientHeight > 4;
  const trackH = rect.height - RAIL_PAD * 2;
  const sceneW = RAIL_WIDTH - BAR_PAD_X * 2;
  // To scale with itself — squash only when the mini doc can't fit the rail
  const squash = Math.min(1, trackH / scene.totalH);

  const docToMini = (docY) =>
    interpolate(scene.anchors, 'doc', 'mini', docY) * squash;

  const viewportTop = docToMini(metrics.scrollTop);
  const viewportH = Math.max(
    10,
    docToMini(metrics.scrollTop + metrics.clientHeight) - viewportTop
  );
  const liveBox = dragBoxRef.current;
  const shownTop = liveBox ? liveBox.top : viewportTop;
  const shownH = liveBox ? liveBox.h : viewportH;

  const startDrag = (e, offset) => {
    const track = trackRef.current;
    if (!track || !scrollEl) return;
    const trackTop = track.getBoundingClientRect().top;
    const max = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
    const map = {
      anchors: scene.anchors,
      squash,
      clientHeight: metrics.clientHeight,
      trackH,
      fixedH: shownH,
    };
    dragRef.current = { active: true };
    let raf = 0;
    let pendingY = e.clientY;
    const apply = (clientY) => {
      const box = boxForPointer({
        pointerY: clientY - trackTop,
        offset,
        trackH: map.trackH,
        anchors: map.anchors,
        squash: map.squash,
        clientHeight: map.clientHeight,
        fixedH: map.fixedH,
      });
      dragBoxRef.current = box;
      const viewport = viewportRef.current;
      if (viewport) {
        viewport.style.top = `${box.top}px`;
        viewport.style.height = `${box.h}px`;
      }
      scrollEl.scrollTop = Math.max(0, Math.min(box.docTop, max));
    };
    const onMove = (ev) => {
      pendingY = ev.clientY;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        apply(pendingY);
      });
    };
    const end = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      if (endDragRef.current === end) endDragRef.current = null;
      dragBoxRef.current = null;
      dragRef.current = null;
      finishDrag();
      setDragging(false);
    };
    endDragRef.current = end;
    apply(e.clientY);
    setDragging(true);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };

  const handleTrackPointerDown = (e) => {
    e.preventDefault();
    const track = trackRef.current;
    if (!track) return;
    startDrag(e, shownH / 2);
  };

  const handleViewportPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const track = trackRef.current;
    if (!track) return;
    const y = e.clientY - track.getBoundingClientRect().top;
    startDrag(e, y - shownTop);
  };

  return createPortal(
    <div
      className={`doc-minimap${dragging ? ' doc-minimap--dragging' : ''}`}
      style={{
        top: rect.top + RAIL_PAD,
        left: rect.right - RAIL_WIDTH - RAIL_PAD,
        width: RAIL_WIDTH,
        height: trackH,
      }}
      aria-hidden="true"
    >
      <div
        ref={trackRef}
        className="doc-minimap__track"
        onPointerDown={handleTrackPointerDown}
        role="presentation"
      >
        <MinimapCanvas
          items={scene.items}
          squash={squash}
          sceneW={sceneW}
          trackH={trackH}
        />
        {scrollable && (
          <div
            ref={viewportRef}
            className="doc-minimap__viewport"
            style={{ top: shownTop, height: shownH }}
            onPointerDown={handleViewportPointerDown}
          />
        )}
      </div>
    </div>,
    document.body
  );
};
